








import { Component, Vue, Watch, } from "@/shared/vuepack";
import * as routes from "@/employee/route-names";
import * as sharedRoutes from "@/shared/route-names";
import { Location } from "vue-router";
import { LoginMethod } from "@/shared/types";
import { isMobileApp } from "@/shared/mobileHelper";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useBiometricAuthStore } from "@/shared/store/biometricAuth";
import { useUserStore } from "@/shared/store/user";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { useFiservDebitCardsStore } from "@/shared/store/fiservDebitCards";

@Component
export default class SettingsNavigation extends Vue {
  custodianSettingsStore = useCustodianSettingsStore();
  featureFlagStore = useFeatureFlagStore();
  biometricAuthStore = useBiometricAuthStore();
  userStore = useUserStore();
  hsabAccountsStore = useHsabAccountsStore();
  fiservDebitCardStore = useFiservDebitCardsStore();

  routes = routes;
  sharedRoutes = sharedRoutes;
  navItems: Map<Location, string> = new Map<Location, string>();

  @Watch("custodianSettingsStore.isLoaded")
  custodianSettingsLoaded () {
    if (this.custodianSettingsStore.isHsaBankCustodian) {
      this.hsabAccountsStore.getHsabAccounts();
    }
    this.updateNavItems();
  }

  @Watch("custodianSettingsStore.isHsaBankCustodian")
  isHsaBankCustodianChanged (newVal, oldVal) {
    if (newVal && !oldVal) {
      this.hsabAccountsStore.getHsabAccounts();
    }
  }

  @Watch("hsabAccountsStore.userHasHsaAccount")
  userHasHsaAccountChanged () {
    this.updateNavItems();
  }

  @Watch("hsabAccountsStore.debitCardsAvailable")
  debitCardsAvailableChanged () {
    this.updateNavItems();
  }

  @Watch("fiservDebitCardStore.fiservDebitCards")
  fiservDebitCardsChanged () {
    this.updateNavItems();
  }

  get activeTab () {
    const routeName = this.$route.name;

    if (routeName === routes.Settings) {
      if (this.userStore.isNotionalDashboardActive) {
        return routes.MyProfileSettings;
      }

      return this.custodianSettingsStore.displayMyProfileEditMode ? routes.MyProfileEdit : routes.MyProfileDisplay;
    }

    if (this.custodianSettingsStore.displayMyProfileEditMode && routeName === routes.MyProfileSettings) {
      return routes.MyProfileEdit;
    }

    return routeName;
  }

  get activeTabIndex () {
    return Array.from(this.navItems.keys()).findIndex(i => i.name === this.activeTab);
  }

  get showDebitCards () {
    return this.custodianSettingsStore.isLoaded &&
      (!this.custodianSettingsStore.isHsaBankCustodian || this.hsabAccountsStore.debitCardsAvailable);
  }

  get showWexDebitCardUi () {
    return this.featureFlagStore.flags.bend20636WexDebitCardUi;
  }

  updateNavItems () {
    const items = new Map<Location, string>();
    this.checkAndAddMyProfileToNav(items);
    this.checkAndAddHsaCardsToNav(items);
    this.checkAndAddWexDebitCardUiToNav(items);
    this.checkAndAddLinkedAccountsToNav(items);
    this.checkAndAddHealthPlansToNav(items);
    this.checkAndAddSecurityToNav(items);
    this.checkAndAddPreferencesToNav(items);
    this.checkAndAddBiometricAuthToNav(items);
    this.navItems = items;
  }

  checkAndAddLinkedAccountsToNav (navItems: Map<Location, string>) {
    if (!this.custodianSettingsStore.displaySettingsLinkedAccounts) {
      return;
    }
    if (this.custodianSettingsStore.isHsaBankCustodian && !this.hsabAccountsStore.userHasHsaAccount) {
      return;
    }
    navItems.set({ name: routes.LinkedAccountsSettings }, "Linked Accounts");
  }

  checkAndAddHealthPlansToNav (navItems: Map<Location, string>) {
    if (this.userStore.partnerDisableTalon) {
      return;
    }
    if (!this.featureFlagStore.flags.bend19525ConsumerClaimsExchangeSettings) {
      if (this.custodianSettingsStore.isHsaBankCustodian && !this.hsabAccountsStore.userHasHsaAccount) {
        return;
      }
    }
    else {
       if (!this.custodianSettingsStore.isHsaBankCustodian) {
         return;
       }
    }
    navItems.set({ name: routes.HealthPlanSettings }, "Health Plan");
  }

  checkAndAddMyProfileToNav (navItems: Map<Location, string>) {
    if (this.custodianSettingsStore.displayMyProfileEditMode) {
      navItems.set({ name: routes.MyProfileEdit }, "My Profile");
    } else {
      navItems.set({ name: routes.MyProfileDisplay }, "My Profile");
    }
  }

  checkAndAddSecurityToNav (navItems: Map<Location, string>) {
    if (!this.showSecurity) {
      return;
    }
    navItems.set({ name: sharedRoutes.Security }, "Security");
  }

  checkAndAddPreferencesToNav (navItems: Map<Location, string>) {
    if (!this.custodianSettingsStore.custodianSupportsUserPreferences) {
      return;
    }
    navItems.set({ name: routes.UserPreferences }, "Preferences");
  }

  checkAndAddHsaCardsToNav (navItems: Map<Location, string>) {
    if (this.custodianSettingsStore.isHsaBankCustodian &&
      (!this.hsabAccountsStore.userHasHsaAccount || !this.showDebitCards)) {
      return;
    }
    if (!this.custodianSettingsStore.manageHsaCardsInPortal) {
      return;
    }
    navItems.set({ name: routes.HsaDebitCards }, "Debit Cards");
  }

  checkAndAddWexDebitCardUiToNav (navItems: Map<Location, string>) {
    if (this.custodianSettingsStore.isHsaBankCustodian && this.showWexDebitCardUi) {
      navItems.set({ name: routes.WexDebitCardUi }, "Debit Cards");
    }
  }

  checkAndAddBiometricAuthToNav (navItems: Map<Location, string>) {
    if (this.showBiometricAuthSettings) {
      navItems.set({ name: routes.BiometricAuthSettings }, "Biometric Login");
    }
  }

  mounted (): void {
    if (this.activeTab === sharedRoutes.Security && this.showSecurity === false) {
      this.$router.push({ name: routes.Settings });
    }

    this.fiservDebitCardStore.getFiservDebitCards();
    this.custodianSettingsStore.getCustodianSettings();
    this.userStore.loadPartnerSettings();
    if (this.canLoadBiometricsSettings) {
      this.biometricAuthStore.loadBiometricSettings();
    }

    // only update nav tab items if custodian data is loaded,
    // tabs also depend on hsab account types and those must be
    // loaded as well before updating tabs displayed,
    // otherwise wait for data to be loaded with watch methods
    if (this.custodianSettingsStore.isLoaded) {
      if (this.custodianSettingsStore.isHsaBankCustodian) {
        if (this.hsabAccountsStore.allHsabAccountsLoaded) {
          this.updateNavItems();
        }
      } else {
        this.updateNavItems();
      }
    }
  }

  get showSecurity (): boolean {
    return (
      this.custodianSettingsStore.allowMfa && this.custodianSettingsStore.displayMFASettings &&
      this.userStore.loginMethod !== LoginMethod.Sso
    );
  }

  get showBiometricAuthSettings (): boolean {
    return this.canLoadBiometricsSettings && this.biometricAuthStore.isBiometricAuthAvailable;
  }

  get canLoadBiometricsSettings () {
    return isMobileApp();
  }

}
